import React from 'react';
import { Conversation } from '../../types';
import { formatMessageTime } from '../../utils/dateUtils';

interface ConversationItemProps {
  conversation: Conversation;
  isSelected: boolean;
  currentUserId: string;
  onSelect: (id: string) => void;
}

export const ConversationItem: React.FC<ConversationItemProps> = ({
  conversation,
  isSelected,
  currentUserId,
  onSelect
}) => {
  const [menuOpen, setMenuOpen] = React.useState(false);

  const otherParticipantId =
    conversation.participantIds.find((id) => id !== currentUserId) || 'Unknown';

  // Determine display name and photo
  let displayName = 'Unknown User';
  let photoUrl: string | null = null;
  let isGuest = false;

  if (
    conversation.metadata?.type === 'inquiry' &&
    conversation.metadata.guestName
  ) {
    // Guest User
    displayName = conversation.metadata.guestName;
    isGuest = true;
  } else if (
    conversation.participants &&
    conversation.participants[otherParticipantId]
  ) {
    // Hydrated User
    const p = conversation.participants[otherParticipantId];
    displayName = p.displayName;
    photoUrl = p.photoUrl;
  } else {
    // Fallback
    displayName = `User ${otherParticipantId.slice(0, 5)}`;
  }

  const initials = displayName.slice(0, 2).toUpperCase();
  const isUnread =
    conversation.unreadCount && typeof conversation.unreadCount === 'object'
      ? (conversation.unreadCount[currentUserId] || 0) > 0
      : (conversation.unreadCount || 0) > 0;

  const handleToggleReadStatus = async (e: React.MouseEvent) => {
    e.stopPropagation();
    // Dynamically import to avoid circular dependency
    const { messageService } = await import('../../api/messageService');
    if (isUnread) {
      await messageService.markAsRead(conversation.id, currentUserId);
    } else {
      await messageService.markAsUnread(conversation.id, currentUserId);
    }
  };

  return (
    <div
      onClick={() => onSelect(conversation.id)}
      className={`
        flex items-center p-3 md:p-4 cursor-pointer active:bg-gray-100 hover:bg-gray-50 transition-colors group relative touch-manipulation
        ${isSelected ? 'bg-blue-50 border-l-4 md:border-l-0 md:border-r-4 border-blue-500' : 'border-b border-gray-100'}
      `}
    >
      {/* 3-dot Menu (All Screens) */}
      <div className="absolute right-2 top-2 z-10">
        <button
          onClick={(e) => {
            e.stopPropagation();
            setMenuOpen(!menuOpen);
          }}
          className="p-2 hover:bg-gray-200 rounded-full transition-colors md:opacity-0 md:group-hover:opacity-100"
          title="Options"
        >
          <svg
            className="w-5 h-5 text-gray-600"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M12 8c1.1 0 2-0.9 2-2s-0.9-2-2-2-2 0.9-2 2 0.9 2 2 2zm0 2c-1.1 0-2 0.9-2 2s0.9 2 2 2 2-0.9 2-2-0.9-2-2-2zm0 6c-1.1 0-2 0.9-2 2s0.9 2 2 2 2-0.9 2-2-0.9-2-2-2z" />
          </svg>
        </button>
        {menuOpen && (
          <div className="absolute right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-md z-20 w-48">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleToggleReadStatus(e as any);
                setMenuOpen(false);
              }}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-2 rounded-lg"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                {isUnread ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                )}
              </svg>
              {isUnread ? 'Mark as read' : 'Mark as unread'}
            </button>
          </div>
        )}
      </div>

      <div className="relative flex-shrink-0">
        {photoUrl ? (
          <img
            src={photoUrl}
            alt={displayName}
            className="h-12 w-12 md:h-14 md:w-14 rounded-full object-cover"
          />
        ) : (
          <div
            className={`h-12 w-12 md:h-14 md:w-14 rounded-full flex items-center justify-center text-sm md:text-base font-bold overflow-hidden ${isGuest ? 'bg-orange-100 text-orange-600' : 'bg-gray-300 text-gray-600'}`}
          >
            {initials}
          </div>
        )}
        {/* Guest Badge */}
        {isGuest && (
          <span className="absolute -bottom-1.5 -right-1.5 bg-orange-500 text-white text-[10px] px-1.5 py-0.5 rounded-full border-2 border-white shadow-sm">
            Guest
          </span>
        )}
        {/* Unread Badge (show on top-right corner) */}
        {isUnread && (
          <span className="absolute top-0 right-0 block h-3 w-3 rounded-full ring-2 ring-white bg-blue-600" />
        )}
      </div>

      <div className="ml-4 flex-1 min-w-0 pr-12">
        <div className="flex justify-between items-baseline mb-1">
          <h3
            className={`text-sm font-semibold truncate ${isUnread ? 'text-black' : 'text-gray-900'} flex items-center gap-2`}
          >
            {displayName}
            {isGuest && (
              <span className="text-xs font-normal text-gray-500 bg-gray-100 px-1.5 rounded">
                Inquiry
              </span>
            )}
          </h3>
          <span className="text-xs text-gray-500 whitespace-nowrap">
            {formatMessageTime(
              conversation.lastMessage?.timestamp || conversation.updatedAt
            )}
          </span>
        </div>
        <p
          className={`text-sm truncate ${isUnread ? 'font-semibold text-gray-900' : 'text-gray-500'}`}
        >
          {conversation.lastMessage ? (
            conversation.lastMessage.senderId === currentUserId ? (
              `You: ${conversation.lastMessage.content}`
            ) : (
              conversation.lastMessage.content
            )
          ) : (
            <span className="italic">No messages yet</span>
          )}
        </p>
      </div>
    </div>
  );
};
